import { useCallback, useEffect, useMemo, useState, type FormEvent } from "react";
import { ArchiveRestore, Clock3, DatabaseBackup, Eye, EyeOff, Gift, Minus, Plus, RefreshCw, Search, ShieldCheck, Trash2, UserPlus } from "lucide-react";
import { Button, EmptyState, ErrorState, Modal, Notice, Skeleton } from "../components/ui";
import { apiRequest, jsonBody } from "../lib/api";
import { formatDateTime, formatRupiah } from "../lib/format";
import { normalizeCollection, type ApiRecord, type SessionUser } from "../lib/types";
import {
  MEMBER_POINT_VALUE_RUPIAH,
  MEMBER_SPEND_PER_POINT_RUPIAH,
  memberPointDiscount,
  memberPointsFromPurchase,
} from "../../shared/loyalty";

const field = (row: ApiRecord, camel: string, snake: string) => row[camel] ?? row[snake];
const asText = (value: unknown) => typeof value === "string" || typeof value === "number" ? String(value) : "";
const asNumber = (value: unknown) => Number.isFinite(Number(value)) ? Number(value) : 0;
const rupiahDigits = (value: string) => value.replace(/\D/gu, "").slice(0, 10);
const formatRupiahInput = (value: string) =>
  value ? new Intl.NumberFormat("id-ID").format(Number(value)) : "";

interface SheetSyncStatus {
  enabled: boolean;
  configured: boolean;
  state: "DISABLED" | "NOT_CONFIGURED" | "IDLE" | "BUSY" | "SUCCESS" | "ERROR";
  status: {
    success?: boolean;
    syncedAt?: string;
    failedAt?: string;
    batchId?: string;
    processedEvents?: number;
    members?: number;
    pointEntries?: number;
    orders?: number;
    message?: string;
  } | null;
  pending: number;
  failed: number;
  processing: number;
  batchSize: number;
}

export function MemberManagementPage({ role }: { role: SessionUser["role"] }) {
  const prefix = role === "OWNER" ? "/api/owner/members" : "/api/admin/members";
  const [members, setMembers] = useState<ApiRecord[]>([]);
  const [archivedMembers, setArchivedMembers] = useState<ApiRecord[]>([]);
  const [ledger, setLedger] = useState<ApiRecord[]>([]);
  const [query, setQuery] = useState("");
  const [selectedId, setSelectedId] = useState("");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string>();
  const [notice, setNotice] = useState<string>();
  const [busy, setBusy] = useState(false);
  const [newName, setNewName] = useState("");
  const [newPhone, setNewPhone] = useState("");
  const [temporary, setTemporary] = useState<{ name: string; phone: string; pin: string }>();
  const [editName, setEditName] = useState("");
  const [showPin, setShowPin] = useState(true);
  const [purchaseAmount, setPurchaseAmount] = useState("");
  const [manualAction, setManualAction] = useState<"ADD" | "SUBTRACT">("ADD");
  const [manualPoints, setManualPoints] = useState("");
  const [reason, setReason] = useState("");
  const [lookupMode, setLookupMode] = useState("ORDER_AND_PHONE");
  const [sheetSync, setSheetSync] = useState<SheetSyncStatus>();
  const [archiving, setArchiving] = useState<ApiRecord>();

  const load = useCallback(async () => {
    setLoading(true);
    setError(undefined);
    try {
      const [memberPayload, archivePayload, ledgerPayload, setting, syncStatus] = await Promise.all([
        apiRequest<unknown>(`${prefix}?view=active&q=${encodeURIComponent(query)}`),
        apiRequest<unknown>(`${prefix}?view=archived&q=${encodeURIComponent(query)}`),
        apiRequest<unknown>(`${prefix}/ledger`),
        role === "OWNER"
          ? apiRequest<{ mode: string }>(`${prefix}/lookup-setting`)
          : Promise.resolve(undefined),
        role === "OWNER"
          ? apiRequest<SheetSyncStatus>(`${prefix}/sync-status`)
          : Promise.resolve(undefined),
      ]);
      setMembers(normalizeCollection(memberPayload).items);
      setArchivedMembers(normalizeCollection(archivePayload).items);
      setLedger(normalizeCollection(ledgerPayload).items);
      if (setting?.mode) setLookupMode(setting.mode);
      if (syncStatus) setSheetSync(syncStatus);
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : "Data member belum dapat dimuat.");
    } finally {
      setLoading(false);
    }
  }, [prefix, query, role]);

  useEffect(() => {
    const timer = window.setTimeout(() => void load(), query ? 250 : 0);
    return () => window.clearTimeout(timer);
  }, [load, query]);

  const selected = useMemo(
    () => members.find((item) => asText(item.id) === selectedId),
    [members, selectedId],
  );
  const selectedLedger = useMemo(
    () => ledger.filter((item) => asText(field(item, "memberId", "member_id")) === selectedId),
    [ledger, selectedId],
  );

  const createMember = async (event: FormEvent) => {
    event.preventDefault();
    setBusy(true);
    setNotice(undefined);
    try {
      const result = await apiRequest<{ name: string; temporaryPin: string; whatsappNumber: string }>(prefix, {
        method: "POST",
        ...jsonBody({ name: newName.trim(), whatsappNumber: newPhone.trim() }),
      });
      setTemporary({ name: result.name, phone: result.whatsappNumber, pin: result.temporaryPin });
      setNewName("");
      setNewPhone("");
      setNotice("Member berhasil dibuat. Berikan PIN sementara ini langsung kepada pelanggan.");
      await load();
    } catch (caught) {
      setNotice(caught instanceof Error ? caught.message : "Member belum berhasil dibuat.");
    } finally {
      setBusy(false);
    }
  };

  const resetPin = async () => {
    if (!selected) return;
    setBusy(true);
    try {
      const result = await apiRequest<{ temporaryPin: string }>(`${prefix}/${selectedId}/reset-pin`, {
        method: "POST",
        ...jsonBody({}),
      });
      setTemporary({
        name: asText(selected.name),
        phone: asText(field(selected, "whatsappNumber", "whatsapp_number")),
        pin: result.temporaryPin,
      });
      setShowPin(true);
      setNotice("PIN lama sudah tidak berlaku. PIN sementara baru hanya ditampilkan di layar ini.");
      await load();
    } catch (caught) {
      setNotice(caught instanceof Error ? caught.message : "PIN belum berhasil direset.");
    } finally {
      setBusy(false);
    }
  };

  const archiveMember = async () => {
    if (!archiving) return;
    const memberId = asText(archiving.id);
    setBusy(true);
    setNotice(undefined);
    try {
      await apiRequest(`${prefix}/${memberId}`, { method: "DELETE", ...jsonBody({}) });
      setArchiving(undefined);
      if (selectedId === memberId) setSelectedId("");
      setNotice("Member dipindahkan ke arsip 30 hari. Login member langsung dinonaktifkan.");
      await load();
    } catch (caught) {
      setNotice(caught instanceof Error ? caught.message : "Member belum berhasil diarsipkan.");
    } finally {
      setBusy(false);
    }
  };

  const restoreMember = async (memberId: string) => {
    setBusy(true);
    setNotice(undefined);
    try {
      await apiRequest(`${prefix}/${memberId}/restore`, {
        method: "POST",
        ...jsonBody({}),
      });
      setNotice("Member berhasil dipulihkan dan dapat login kembali.");
      await load();
    } catch (caught) {
      setNotice(caught instanceof Error ? caught.message : "Member belum berhasil dipulihkan.");
    } finally {
      setBusy(false);
    }
  };

  const saveName = async () => {
    if (!selected || editName.trim().length < 2) return;
    setBusy(true);
    setNotice(undefined);
    try {
      await apiRequest(`${prefix}/${selectedId}`, {
        method: "PATCH",
        ...jsonBody({ name: editName.trim() }),
      });
      setNotice("Nama member berhasil diperbarui dan siap disinkronkan.");
      await load();
    } catch (caught) {
      setNotice(caught instanceof Error ? caught.message : "Nama member belum berhasil disimpan.");
    } finally {
      setBusy(false);
    }
  };

  const updatePoints = async (kind: "PURCHASE" | "MANUAL") => {
    if (!selected) return;
    setBusy(true);
    setNotice(undefined);
    try {
      const payload = kind === "PURCHASE"
        ? { action: "PURCHASE", purchaseAmount: Number(purchaseAmount), reason: "Belanja langsung di toko" }
        : { action: manualAction, points: Number(manualPoints), reason: reason.trim() };
      await apiRequest(`${prefix}/${selectedId}/points`, {
        method: "POST",
        ...jsonBody(payload),
      });
      setPurchaseAmount("");
      setManualPoints("");
      setReason("");
      setNotice("Poin member berhasil diperbarui dan alasannya tersimpan.");
      await load();
    } catch (caught) {
      setNotice(caught instanceof Error ? caught.message : "Poin belum berhasil diperbarui.");
    } finally {
      setBusy(false);
    }
  };

  const saveLookupMode = async () => {
    setBusy(true);
    try {
      await apiRequest(`${prefix}/lookup-setting`, {
        method: "PUT",
        ...jsonBody({ mode: lookupMode }),
      });
      setNotice("Cara pelanggan mengecek pesanan berhasil diperbarui.");
    } catch (caught) {
      setNotice(caught instanceof Error ? caught.message : "Pengaturan belum tersimpan.");
    } finally {
      setBusy(false);
    }
  };

  const syncNow = async () => {
    setBusy(true);
    setNotice(undefined);
    try {
      const result = await apiRequest<{
        state: string;
        processedEvents: number;
        members: number;
        pointEntries: number;
        orders: number;
        message: string;
      }>(`${prefix}/sync-now`, { method: "POST", ...jsonBody({}) });
      setNotice(result.state === "IDLE"
        ? "Sinkron selesai. Tidak ada perubahan baru yang perlu dikirim."
        : `Cadangan Google Sheet berhasil diperbarui dalam satu batch: ${result.members} member, ${result.pointEntries} catatan poin, dan ${result.orders} pesanan.`);
      await load();
    } catch (caught) {
      setNotice(caught instanceof Error ? caught.message : "Cadangan Google Sheet belum berhasil diperbarui.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="dashboard-page">
      <div className="dashboard-heading">
        <div>
          <span className="eyebrow">Loyalitas pelanggan</span>
          <h1>Member & Poin</h1>
          <p>
            Semua cabang memakai satu data member. Belanja{" "}
            {formatRupiah(MEMBER_SPEND_PER_POINT_RUPIAH)} = 1 poin; 1 poin ={" "}
            {formatRupiah(MEMBER_POINT_VALUE_RUPIAH)}.
          </p>
        </div>
        <Button variant="secondary" onClick={() => void load()} icon={<RefreshCw aria-hidden="true" />}>Perbarui</Button>
      </div>
      {notice && <Notice tone={notice.includes("berhasil") || notice.includes("selesai") || notice.includes("hanya ditampilkan") ? "success" : "danger"}>{notice}</Notice>}
      {temporary && (
        <Notice tone="warning">
          <strong>{temporary.phone}</strong> — PIN sementara: <b>{showPin ? temporary.pin : "•••••"}</b>{" "}
          <button type="button" className="inline-icon-button" onClick={() => setShowPin((value) => !value)}>
            {showPin ? <EyeOff aria-hidden="true" /> : <Eye aria-hidden="true" />} {showPin ? "Sembunyikan" : "Tampilkan"}
          </button>
          <br />Pelanggan wajib menukarnya melalui menu Member di ponsel. PIN lama tidak dapat ditampilkan karena tidak disimpan sebagai teks.
        </Notice>
      )}

      {role === "OWNER" && (
        <>
          <section className="dashboard-panel member-setting-panel">
            <div>
              <h2>Cara Cek Pesanan</h2>
              <p>Pilihan nomor pesanan + WhatsApp paling aman untuk data pelanggan.</p>
            </div>
            <select value={lookupMode} onChange={(event) => setLookupMode(event.target.value)}>
              <option value="ORDER_AND_PHONE">Nomor pesanan + WhatsApp</option>
              <option value="ORDER_ONLY">Nomor pesanan saja</option>
              <option value="PHONE_ONLY">WhatsApp saja (pesanan terbaru)</option>
            </select>
            <Button onClick={() => void saveLookupMode()} busy={busy}>Simpan Pilihan</Button>
          </section>
          <section className="dashboard-panel member-setting-panel">
            <div>
              <h2>Cadangan Google Sheet</h2>
              <p>
                D1 tetap data utama. Sheet <strong>datamemberSedapSayur</strong> adalah salinan kedua tanpa PIN, password, sesi login, dan alamat pesanan.
              </p>
              <div className="sync-metrics">
                <span><small>Status</small><strong>{!sheetSync?.enabled ? "Nonaktif" : sheetSync.state === "ERROR" ? "Error" : sheetSync.configured ? "Terhubung" : "Belum terhubung"}</strong></span>
                <span><small>Terakhir berhasil</small><strong>{sheetSync?.status?.syncedAt ? formatDateTime(sheetSync.status.syncedAt) : "Belum ada"}</strong></span>
                <span><small>Pending</small><strong>{sheetSync?.pending ?? 0}</strong></span>
                <span><small>Gagal</small><strong>{sheetSync?.failed ?? 0}</strong></span>
                <span><small>Batch terakhir</small><strong>{sheetSync?.status?.batchId ? sheetSync.status.batchId.slice(0, 8) : "—"}</strong></span>
                <span><small>Batas batch</small><strong>{sheetSync?.batchSize ?? 100} event</strong></span>
              </div>
              {sheetSync?.status?.message && <small>{sheetSync.status.message}</small>}
            </div>
            <Button onClick={() => void syncNow()} busy={busy} disabled={!sheetSync?.enabled || !sheetSync?.configured} icon={<DatabaseBackup aria-hidden="true" />}>
              Sinkron Sekarang
            </Button>
          </section>
        </>
      )}

      <div className="member-admin-grid">
        <section className="dashboard-panel">
          <h2>Tambah Member dari Toko</h2>
          <form className="inline-form" onSubmit={(event) => void createMember(event)}>
            <label>
              Nama lengkap
              <input value={newName} onChange={(event) => setNewName(event.target.value)} minLength={2} maxLength={100} placeholder="Contoh: Ibu Ayu" required />
            </label>
            <label>
              Nomor WhatsApp
              <input inputMode="tel" value={newPhone} onChange={(event) => setNewPhone(event.target.value)} placeholder="+628..., 08..., atau +61..." required />
            </label>
            <Button type="submit" busy={busy} icon={<UserPlus aria-hidden="true" />}>Buat Member</Button>
          </form>
        </section>
        <section className="dashboard-panel">
          <div className="member-list-heading">
            <div>
              <h2>Member Aktif</h2>
              <small>{members.length} member ditemukan</small>
            </div>
          </div>
          <label className="dashboard-search">
            <Search aria-hidden="true" />
            <input value={query} onChange={(event) => setQuery(event.target.value)} placeholder="Cari nama atau nomor WhatsApp…" />
          </label>
          {loading && <Skeleton rows={5} />}
          {error && <ErrorState message={error} onRetry={() => void load()} />}
          {!loading && !error && members.length === 0 && <EmptyState title="Belum ada member" />}
          <div className="member-admin-list">
            {members.map((item) => {
              const id = asText(item.id);
              const points = asNumber(field(item, "pointsBalance", "points_balance"));
              return (
                <button type="button" key={id} className={id === selectedId ? "active" : ""} onClick={() => { setSelectedId(id); setEditName(asText(item.name)); }}>
                  <span><strong>{asText(item.name) || "Nama belum diisi"}</strong><small>{asText(field(item, "whatsappNumber", "whatsapp_number"))} · {asNumber(field(item, "completedOrders", "completed_orders"))} pesanan selesai</small></span>
                  <b>{points} poin</b>
                </button>
              );
            })}
          </div>
        </section>

        <section className="dashboard-panel member-archive-panel">
          <div className="member-list-heading">
            <div>
              <h2>Arsip Member</h2>
              <small>Cadangan sementara selama 30 hari</small>
            </div>
            <Clock3 aria-hidden="true" />
          </div>
          <p className="member-archive-explanation">
            Member di sini tidak dapat login. Pulihkan sebelum tanggal hapus permanen bila penghapusan tidak sengaja.
          </p>
          {loading && <Skeleton rows={4} />}
          {!loading && archivedMembers.length === 0 && (
            <EmptyState title="Arsip masih kosong" description="Member yang dihapus akan tampil di sini selama 30 hari." />
          )}
          <div className="member-archive-list">
            {archivedMembers.map((item) => {
              const id = asText(item.id);
              const expiresAt = asText(field(item, "archiveExpiresAt", "archive_expires_at"));
              return (
                <article key={id}>
                  <div>
                    <strong>{asText(item.name) || "Nama belum diisi"}</strong>
                    <small>{asText(field(item, "whatsappNumber", "whatsapp_number"))}</small>
                    <small>Hapus permanen: {formatDateTime(expiresAt)}</small>
                  </div>
                  <Button
                    variant="secondary"
                    onClick={() => void restoreMember(id)}
                    busy={busy}
                    icon={<ArchiveRestore aria-hidden="true" />}
                  >
                    Pulihkan
                  </Button>
                </article>
              );
            })}
          </div>
        </section>

        <section className="dashboard-panel member-detail-panel">
          {!selected ? (
            <EmptyState title="Pilih member" description="Pilih nomor pelanggan untuk menambah, mengurangi, atau melihat riwayat poin." />
          ) : (
            <>
              <div className="member-detail-heading">
                <div><span>Member</span><h2>{asText(selected.name) || "Nama belum diisi"}</h2><small>{asText(field(selected, "whatsappNumber", "whatsapp_number"))}</small></div>
                <strong><Gift aria-hidden="true" /> {asNumber(field(selected, "pointsBalance", "points_balance"))} poin</strong>
              </div>
              <div className="member-name-editor">
                <label>Nama member<input value={editName} onChange={(event) => setEditName(event.target.value)} minLength={2} maxLength={100} placeholder="Isi nama pelanggan" /></label>
                <Button variant="secondary" onClick={() => void saveName()} disabled={editName.trim().length < 2} busy={busy}>Simpan Nama</Button>
              </div>
              <div className="member-account-actions">
                <Button variant="secondary" onClick={() => void resetPin()} busy={busy} icon={<ShieldCheck aria-hidden="true" />}>Reset PIN Member</Button>
                <Button variant="danger" onClick={() => setArchiving(selected)} disabled={busy} icon={<Trash2 aria-hidden="true" />}>Hapus Member</Button>
              </div>
              <div className="points-action-grid">
                <div>
                  <h3>Belanja di toko</h3>
                  <label>Jumlah belanja (Rupiah)<input type="text" inputMode="numeric" value={formatRupiahInput(purchaseAmount)} onChange={(event) => setPurchaseAmount(rupiahDigits(event.target.value))} placeholder="100.000" /></label>
                  <small>
                    Otomatis: {memberPointsFromPurchase(Number(purchaseAmount || 0))} poin
                    {" • nilai potongan "}
                    {formatRupiah(
                      memberPointDiscount(
                        memberPointsFromPurchase(Number(purchaseAmount || 0)),
                      ),
                    )}
                  </small>
                  <Button onClick={() => void updatePoints("PURCHASE")} disabled={Number(purchaseAmount) < MEMBER_SPEND_PER_POINT_RUPIAH} busy={busy}>Tambahkan dari Belanja</Button>
                </div>
                <div>
                  <h3>Ubah manual</h3>
                  <select value={manualAction} onChange={(event) => setManualAction(event.target.value as "ADD" | "SUBTRACT")}>
                    <option value="ADD">Tambah poin</option><option value="SUBTRACT">Kurangi / tukar hadiah</option>
                  </select>
                  <label>Jumlah poin<input type="number" min="1" value={manualPoints} onChange={(event) => setManualPoints(event.target.value)} /></label>
                  <label>Alasan<input value={reason} onChange={(event) => setReason(event.target.value)} placeholder="Contoh: Tukar hadiah sayur" /></label>
                  <Button onClick={() => void updatePoints("MANUAL")} disabled={!manualPoints || reason.trim().length < 3} busy={busy} icon={manualAction === "ADD" ? <Plus aria-hidden="true" /> : <Minus aria-hidden="true" />}>Simpan Perubahan</Button>
                </div>
              </div>
              <h3>Riwayat & alasan</h3>
              {selectedLedger.length === 0 ? <EmptyState title="Belum ada riwayat poin" /> : (
                <div className="point-ledger-list">
                  {selectedLedger.map((item) => {
                    const change = asNumber(field(item, "pointsChange", "points_change"));
                    return <div key={asText(item.id)}><span><strong>{asText(field(item, "reason", "reason"))}</strong><small>{asText(field(item, "branchName", "branch_name")) || "Online / Owner"} · {formatDateTime(asText(field(item, "createdAt", "created_at")))}</small>{field(item, "purchaseAmount", "purchase_amount") ? <small>Belanja {formatRupiah(asNumber(field(item, "purchaseAmount", "purchase_amount")))}</small> : null}</span><b className={change > 0 ? "positive" : "negative"}>{change > 0 ? "+" : ""}{change} poin</b></div>;
                  })}
                </div>
              )}
            </>
          )}
        </section>
      </div>
      <Modal
        open={Boolean(archiving)}
        onClose={() => setArchiving(undefined)}
        title="Hapus member?"
        description={`${asText(archiving?.name) || "Member ini"} akan masuk Arsip Member selama 30 hari.`}
      >
        <Notice tone="warning">
          Login langsung dinonaktifkan. Selama 30 hari, Admin atau Owner masih dapat memulihkannya. Setelah itu identitas akun dihapus permanen dan tidak dapat dikembalikan.
        </Notice>
        <div className="modal-actions">
          <Button type="button" variant="secondary" onClick={() => setArchiving(undefined)}>Batal</Button>
          <Button type="button" variant="danger" onClick={() => void archiveMember()} busy={busy} icon={<Trash2 aria-hidden="true" />}>Hapus ke Arsip</Button>
        </div>
      </Modal>
    </div>
  );
}
